// src/app/pages/app/LeveragePage.jsx
import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, Clock, Zap, RefreshCw, ArrowRight } from "lucide-react";
import { getWalletOverview } from "../../features/wallet/walletApi.js";
import { getLeverageEligibility, getMyLeverage, requestLeverage } from "../../features/leverage/leverageApi.js";
import { useNavigate } from "react-router-dom";

function format(n, d = 2) {
  const num = Number(n || 0);
  return num.toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: d });
}

function Card({ title, sub, right, children }) {
  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 rounded-2xl p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-white">{title}</div>
          {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
        </div>
        {right}
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

export default function LeveragePage() {
  const qc = useQueryClient();
  const nav = useNavigate();

  const [asset, setAsset] = useState("USDT");
  const [network, setNetwork] = useState("MAINNET");

  // Wallet (source of truth for what user actually has)
  const { data: wallet, isFetching: fetchingWallet, refetch: refetchWallet } = useQuery({
    queryKey: ["walletOverview"],
    queryFn: getWalletOverview,
    staleTime: 10_000,
  });

  // Active leverage grant
  const { data: myLev, isFetching: fetchingMe, refetch: refetchMe } = useQuery({
    queryKey: ["leverageMe"],
    queryFn: getMyLeverage,
    staleTime: 5_000,
  });

  // Eligibility quote
  const { data: elig, isFetching: fetchingElig, refetch: refetchElig } = useQuery({
    queryKey: ["leverageEligibility", asset, network],
    queryFn: () => getLeverageEligibility(asset, network),
    staleTime: 5_000,
  });

  const reqMut = useMutation({
    mutationFn: requestLeverage,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["leverageMe"] });
      await qc.invalidateQueries({ queryKey: ["leverageEligibility"] });
    },
  });

  const balances = useMemo(() => wallet?.balances || [], [wallet]);
  const assetOptions = useMemo(() => {
    const set = new Set(balances.map((b) => b.asset));
    const list = Array.from(set);
    if (!list.includes("USDT")) list.unshift("USDT");
    return list.sort();
  }, [balances]);

  const available = useMemo(() => {
    // expects wallet overview balances: [{asset, network, available, locked}]
    const b = balances.find((x) => x.asset === asset && x.network === network);
    return Number(b?.available || 0);
  }, [balances, asset, network]);

  const maxPct = Number(elig?.max_pct || 0);
  const suggestedCredit = Number(elig?.suggested_credit || 0);
  const tradingPower = Number(elig?.trading_power || (available + suggestedCredit));

  const hasActive = Boolean(myLev?.hasActive);

  const onRefresh = async () => {
    await Promise.all([refetchWallet(), refetchMe(), refetchElig()]);
  };

  const reqErr =
    reqMut.error?.response?.data?.detail ||
    reqMut.error?.message ||
    "";

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary-600 to-cyan-500 grid place-items-center text-white">
              <Zap size={18} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Leverage</h1>
              <p className="text-sm text-gray-400">
                Increase trading power for 24 hours. Credit is recoverable after expiry.
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={onRefresh}
          className="px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-200 text-sm flex items-center gap-2"
          disabled={fetchingWallet || fetchingMe || fetchingElig}
        >
          <RefreshCw size={16} className={(fetchingWallet || fetchingMe || fetchingElig) ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Controls */}
      <div className="mt-6 grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-5">
          <Card
            title="Select balance source"
            sub="Leverage is computed from your available wallet balance."
            right={
              <div className="flex items-center gap-2 text-emerald-300">
                <ShieldCheck size={16} />
                <span className="text-xs">Policy enforced server-side</span>
              </div>
            }
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-gray-400 mb-1">Asset</div>
                <select
                  value={asset}
                  onChange={(e) => setAsset(e.target.value)}
                  className="w-full rounded-lg bg-gray-900/50 border border-gray-800 px-3 py-2 text-white"
                >
                  {assetOptions.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </div>

              <div>
                <div className="text-xs text-gray-400 mb-1">Network</div>
                <select
                  value={network}
                  onChange={(e) => setNetwork(e.target.value)}
                  className="w-full rounded-lg bg-gray-900/50 border border-gray-800 px-3 py-2 text-white"
                >
                  <option value="MAINNET">MAINNET</option>
                  <option value="ETH">ETH</option>
                  <option value="BTC">BTC</option>
                  <option value="TRON">TRON</option>
                </select>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-gray-900/40 border border-gray-800 p-4">
                <div className="text-xs text-gray-400">Available</div>
                <div className="text-white font-extrabold mt-1">{format(available, asset === "BTC" ? 6 : 2)} {asset}</div>
              </div>
              <div className="rounded-xl bg-gray-900/40 border border-gray-800 p-4">
                <div className="text-xs text-gray-400">Max leverage</div>
                <div className="text-white font-extrabold mt-1">{format(maxPct * 100, 0)}%</div>
                <div className="text-[11px] text-gray-500 mt-1">{elig?.auto_approve ? "Auto-approve tier" : "May require approval"}</div>
              </div>
              <div className="rounded-xl bg-gray-900/40 border border-gray-800 p-4">
                <div className="text-xs text-gray-400">Suggested credit</div>
                <div className="text-white font-extrabold mt-1">{format(suggestedCredit, 2)} {asset}</div>
              </div>
            </div>

            {elig?.reason && (
              <div className="mt-4 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                {elig.reason}
              </div>
            )}
          </Card>
        </div>

        <div className="col-span-12 lg:col-span-7">
          <Card
            title="Your leverage status"
            sub="If approved, you get extra credit for 24 hours; it expires automatically."
            right={
              <button
                onClick={() => nav("/app/trade")}
                className="px-3 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-200 text-sm flex items-center gap-2"
              >
                Go to Trade <ArrowRight size={16} />
              </button>
            }
          >
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 md:col-span-6 rounded-2xl bg-gray-900/40 border border-gray-800 p-5">
                <div className="text-xs text-gray-400">Trading power (est.)</div>
                <div className="text-3xl font-extrabold text-white mt-2">
                  {format(tradingPower, 2)} {asset}
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  Wallet available + approved credit (if any).
                </div>
              </div>

              <div className="col-span-12 md:col-span-6 rounded-2xl bg-gray-900/40 border border-gray-800 p-5">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-400">Active grant</div>
                  <div className={`text-xs font-bold px-2 py-1 rounded-lg border ${
                    hasActive
                      ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/20"
                      : "text-gray-300 bg-gray-800/40 border-gray-700"
                  }`}>
                    {hasActive ? "ACTIVE" : "NONE"}
                  </div>
                </div>

                {hasActive ? (
                  <div className="mt-3 space-y-1">
                    <div className="text-white font-semibold">
                      Credit: {format(myLev.creditAmount, 2)} {myLev.asset}
                    </div>
                    <div className="text-xs text-gray-400">
                      Leverage: {format((myLev.pct || 0) * 100, 0)}%
                    </div>
                    <div className="text-xs text-gray-400 flex items-center gap-2 mt-2">
                      <Clock size={14} />
                      Expires: {new Date(myLev.expiresAt).toLocaleString()}
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 text-sm text-gray-400">
                    No active leverage. You can request one based on your balance tier.
                  </div>
                )}
              </div>
            </div>

            {reqErr && (
              <div className="mt-4 text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                {reqErr}
              </div>
            )}

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <button
                disabled={hasActive || reqMut.isPending || !elig?.eligible}
                onClick={() => reqMut.mutate({ asset, network })}
                className="flex-1 rounded-xl bg-gradient-to-r from-primary-600 to-cyan-600 hover:from-primary-700 hover:to-cyan-700 disabled:opacity-60 transition font-semibold py-3 text-white"
              >
                {reqMut.isPending ? "Submitting…" : (elig?.auto_approve ? "Activate Leverage (Auto)" : "Request Leverage")}
              </button>

              <button
                onClick={onRefresh}
                className="sm:w-[180px] rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-200 font-semibold py-3"
              >
                Refresh Status
              </button>
            </div>

            <div className="mt-4 text-xs text-gray-500">
              Note: for low balance tiers, leverage requests remain pending until a moderator approves.
              For high balance tiers, the system auto-approves immediately.
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
